import React, { ReactNode, isValidElement } from 'react'
import { cn } from 'src/shared/components/ui/utils'

type IconCardColor = 'default' | 'green' | 'blue' | 'red' | 'orange'

interface IconCardProps {
  /** Icon element to display in the badge (e.g., <Plus className="w-4 h-4" />) */
  Icon: ReactNode
  /** Color variant of the badge */
  color?: IconCardColor
  /** Size of the badge */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Additional className for the container */
  className?: string

  /** Whether to show shadow (default: true) */
  shadow?: boolean
  /** Border radius variant (default: '2xl') */
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'
  /** Whether to show hover effect (default: true) */
  hover?: boolean
}

const colorStyles: Record<
  IconCardColor,
  { bg: string; shadow: string; icon: string }
> = {
  default: {
    bg: 'bg-gradient-to-br from-gray-100 to-gray-200',
    shadow:
      'shadow-[0_4px_14px_0_rgba(0,0,0,0.15),0_2px_4px_0_rgba(0,0,0,0.1)]',
    icon: 'text-gray-600',
  },
  green: {
    bg: 'bg-gradient-to-br from-green-100 to-green-200',
    shadow:
      'shadow-[0_4px_14px_0_rgba(34,197,94,0.25),0_2px_4px_0_rgba(34,197,94,0.15)]',
    icon: 'text-green-600',
  },
  blue: {
    bg: 'bg-gradient-to-br from-blue-100 to-blue-200',
    shadow:
      'shadow-[0_4px_14px_0_rgba(59,130,246,0.25),0_2px_4px_0_rgba(59,130,246,0.15)]',
    icon: 'text-blue-600',
  },
  red: {
    bg: 'bg-gradient-to-br from-red-100 to-red-200',
    shadow:
      'shadow-[0_4px_14px_0_rgba(239,68,68,0.25),0_2px_4px_0_rgba(239,68,68,0.15)]',
    icon: 'text-red-600',
  },
  orange: {
    bg: 'bg-gradient-to-br from-orange-100 to-orange-200',
    shadow:
      'shadow-[0_4px_14px_0_rgba(249,115,22,0.25),0_2px_4px_0_rgba(249,115,22,0.15)]',
    icon: 'text-orange-600',
  },
}

const sizeStyles = {
  sm: {
    container: 'w-12 h-12',
    icon: 'w-5 h-5',
  },
  md: {
    container: 'w-16 h-16',
    icon: 'w-7 h-7',
  },
  lg: {
    container: 'w-20 h-20',
    icon: 'w-9 h-9',
  },
  xl: {
    container: 'w-28 h-28',
    icon: 'w-11 h-11',
  },
}

// Helper function to safely render an icon (handles both JSX elements and component objects)
const renderIcon = (
  icon: ReactNode,
  defaultClassName: string = 'w-7 h-7'
): ReactNode => {
  if (!icon) return null

  // If it's already a valid React element (JSX), return it as-is
  if (isValidElement(icon)) {
    return icon
  }

  // If it's a function (component), render it as a component
  // This handles function components, class components, forwardRef, memo, etc.
  if (typeof icon === 'function') {
    try {
      return React.createElement(
        icon as React.ComponentType<{ className?: string }>,
        {
          className: defaultClassName,
        }
      )
    } catch (error) {
      // If rendering fails, return null to prevent crashes
      console.warn('Failed to render icon component:', error)
      return null
    }
  }

  // Check if it's an object with a render method (class component instance)
  if (
    icon &&
    typeof icon === 'object' &&
    'render' in icon &&
    typeof (icon as any).render === 'function'
  ) {
    try {
      return React.createElement(
        (icon as any).render as React.ComponentType<{ className?: string }>,
        {
          className: defaultClassName,
        }
      )
    } catch (error) {
      console.warn('Failed to render icon component:', error)
      return null
    }
  }

  // Otherwise, return as-is (for strings, numbers, etc.)
  return icon
}

export const IconCard = ({
  Icon,
  color = 'default',
  size = 'md',
  className,
  shadow = true,
  rounded = 'full',
  hover = false,
}: IconCardProps) => {
  const colors = colorStyles[color]
  const sizes = sizeStyles[size]

  return (
    <div
      className={cn(
        'relative flex items-center justify-center',
        hover ? 'transition-transform duration-200 hover:scale-105' : '',
        shadow ? 'shadow-new' : '',
        rounded ? `rounded-${rounded}` : '',
        'border border-white/50',
        colors.bg,
        // colors.shadow,
        sizes.container,
        className
      )}
    >
      {/* Inner highlight for 3D effect */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/30 to-transparent pointer-events-none" />

      {/* Icon */}
      <div
        className={cn(
          'relative z-10 flex items-center justify-center',
          colors.icon
        )}
      >
        {renderIcon(Icon, sizes.icon)}
      </div>
    </div>
  )
}
